import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ANALYTICS_PAGE_NAMES } from '../Common/Analytics/utils';
import { usePageViewTracking } from '../Common/Hooks';
import useIncorrectPlatformFallback from './hooks/useIncorrectPlatformFallback';

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 100dvh;
  width: 100%;
  overflow-x: hidden;
  margin: 0 auto;
`;

const Hero = styled.header`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  isolation: isolate;
  padding: 112px 30px 80px;
  color: #ffffff;
  text-align: center;

  &::before {
    position: absolute;
    z-index: -1;
    inset: 0;

    background: linear-gradient(
      119.92deg,
      #8a2387 10%,
      #7650aa 46%,
      #5276c8 86%,
      #15c1ff 125%
    );
    clip-path: url('#kindred-partial-permissions-curve');
    content: '';
  }

  @media (min-width: 600px) {
    padding: clamp(136px, 16vw, 176px) 48px clamp(104px, 12vw, 136px);
  }

  @media (max-width: 359px) {
    padding: 88px 20px 64px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    padding-block: 72px 52px;
  }
`;

const CurveDefinition = styled.svg`
  position: absolute;
  width: 0;
  height: 0;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-family: 'Fira Sans', sans-serif;
  font-size: 35px;
  font-weight: 700;
  font-style: normal;
  line-height: 115%;
  letter-spacing: 0;

  span {
    display: block;
  }

  @media (min-width: 600px) {
    max-width: 760px;
    font-size: clamp(38px, 5.2vw, 48px);
  }

  @media (max-width: 359px) {
    font-size: 26px;
  }
`;

const HeroSubtitle = styled.p`
  max-width: 360px;
  margin: 24px 0 0;
  font-family: 'Fira Sans', sans-serif;
  font-weight: 400;
  font-size: 22px;
  font-style: normal;
  line-height: 125%;
  letter-spacing: 0;

  span,
  strong {
    display: block;
  }

  strong {
    font-weight: 700;
  }

  @media (min-width: 600px) {
    max-width: 620px;
    font-size: clamp(22px, 3vw, 26px);
  }

  @media (max-width: 359px) {
    max-width: 300px;
    margin-top: 20px;
    font-size: 16px;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  padding: 0 clamp(20px, 5vw, 48px) 40px;
`;

const InfoTitle = styled.h3`
  color: #323a68;
  font-size: clamp(22px, 3.5vw, 32px);
  font-weight: 700;
  line-height: 1.2;
  margin: clamp(20px, 4vw, 36px) 0 clamp(20px, 3vw, 28px);
`;

const ActivationStepsContainer = styled.div`
  display: flex;
  align-items: stretch;
  gap: clamp(16px, 8vw, 70px);
  height: clamp(280px, 48vw, 360px);
  width: 100%;

  & > img {
    height: 100%;
    width: auto;
    max-width: 20%;
    flex: 0 0 auto;
    object-fit: contain;
  }

  & > ol {
    display: grid;
    flex: 1 1 auto;
    min-width: 0;
    grid-template-rows: 17.5% 21.5% 21.5% 22% 17.5%;
    box-sizing: border-box;
    padding: 0 0 0 24px;
    margin: 0;
    list-style-type: decimal;
    font-size: clamp(15px, 2.6vw, 22px);
    font-weight: 400;

    & > li {
      align-self: center;
      line-height: 1.5;
      margin: 0;
      white-space: pre-line;
    }
  }

  @media (max-width: 359px) {
    gap: 12px;
    height: 260px;

    & > ol {
      padding-left: 20px;
      font-size: 14px;
    }
  }
`;

const InfoContentWrapper = styled.div`
  width: 100%;
  max-width: 760px;
  text-align: left;
`;

const KindredPluginActivationPartialPermissions = () => {
  const { t } = useTranslation();
  const { partially_installed_page } = ANALYTICS_PAGE_NAMES;
  usePageViewTracking(partially_installed_page, 'Kindred');

  useIncorrectPlatformFallback();

  const infoItems = [
    t('KindredPluginActivation.partialPermissions.steps.goToSettings'),
    t('KindredPluginActivation.partialPermissions.steps.tapSafari'),
    t('KindredPluginActivation.partialPermissions.steps.scrollToExtensions'),
    t('KindredPluginActivation.partialPermissions.steps.selectKindred'),
    t('KindredPluginActivation.partialPermissions.steps.allowWebsites'),
  ];

  return (
    <Container>
      <CurveDefinition aria-hidden="true">
        <defs>
          <clipPath
            id="kindred-partial-permissions-curve"
            clipPathUnits="objectBoundingBox"
          >
            <path d="M 0 0 H 1 V 0.72 Q 0.5 1, 0 0.72 Z" />
          </clipPath>
        </defs>
      </CurveDefinition>
      <Hero>
        <HeroTitle>
          <span>
            {t('KindredPluginActivation.partialPermissions.titleFirst')}
          </span>
          <span>
            {t('KindredPluginActivation.partialPermissions.titleSecond')}
          </span>
        </HeroTitle>
        <HeroSubtitle>
          <span>
            {t('KindredPluginActivation.partialPermissions.subtitleFirst')}
          </span>
          <span>
            {t('KindredPluginActivation.partialPermissions.subtitleSecond')}
          </span>
          <strong>
            {t('KindredPluginActivation.partialPermissions.enableExtension')}
          </strong>
        </HeroSubtitle>
      </Hero>

      <InfoContainer>
        <InfoContentWrapper>
          <InfoTitle>
            {t('KindredPluginActivation.partialPermissions.infoTitle')}
          </InfoTitle>
          <ActivationStepsContainer>
            <ol>
              {infoItems?.map((item, index) => (
                <li key={index}>{`${item}`}</li>
              ))}
            </ol>
            <img
              src={require('../Assets/KindredPluginActivation/partialPermissions.png')}
              alt="Kindred extension permission settings"
            />
          </ActivationStepsContainer>
        </InfoContentWrapper>
      </InfoContainer>
    </Container>
  );
};

export default KindredPluginActivationPartialPermissions;
